/**
 * ScrollTimePicker
 *
 * Composes TimeScrollColumn instances (hour + minute) with AM/PM toggle.
 * Displays 12-hour format but communicates 24-hour values externally.
 * Minutes use 5-minute intervals.
 */

import { useCallback, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { Colors } from '@/constants/theme';

import { AmPmToggle } from './AmPmToggle';
import { TimeScrollColumn } from './TimeScrollColumn';

const DISPLAY_HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

type ScrollTimePickerProps = {
  hour: number; // 0–23
  minute: number;
  onTimeChange: (hour: number, minute: number) => void;
  accentColor: string;
  palette: typeof Colors.light;
};

/** Convert 24h hour to 12h display hour (1–12) */
function to12Hour(hour24: number): number {
  const h = hour24 % 12;
  return h === 0 ? 12 : h;
}

/** Convert 12h display hour + period back to 24h */
function to24Hour(displayHour: number, isAm: boolean): number {
  if (isAm) {
    return displayHour === 12 ? 0 : displayHour;
  }
  return displayHour === 12 ? 12 : displayHour + 12;
}

export function ScrollTimePicker({
  hour,
  minute,
  onTimeChange,
  accentColor,
  palette,
}: ScrollTimePickerProps) {
  const isAm = hour < 12;
  const displayHour = to12Hour(hour);

  const formatHour = useCallback((value: number) => {
    return String(value);
  }, []);

  const formatMinute = useCallback((value: number) => {
    return String(value).padStart(2, '0');
  }, []);

  const snappedMinute = useMemo(() => {
    const rounded = Math.round(minute / 5) * 5;
    return rounded >= 60 ? 55 : rounded;
  }, [minute]);

  const handleHourChange = useCallback(
    (newDisplayHour: number) => {
      onTimeChange(to24Hour(newDisplayHour, isAm), snappedMinute);
    },
    [isAm, snappedMinute, onTimeChange],
  );

  const handleMinuteChange = useCallback(
    (newMinute: number) => {
      onTimeChange(hour, newMinute);
    },
    [hour, onTimeChange],
  );

  const handlePeriodChange = useCallback(
    (newIsAm: boolean) => {
      onTimeChange(to24Hour(displayHour, newIsAm), snappedMinute);
    },
    [displayHour, snappedMinute, onTimeChange],
  );

  return (
    <View style={styles.container}>
      <TimeScrollColumn
        values={DISPLAY_HOURS}
        selectedValue={displayHour}
        onValueChange={handleHourChange}
        formatValue={formatHour}
        accentColor={accentColor}
        textColor={palette.text}
        textMutedColor={palette.textMuted}
        cardBackgroundColor={palette.cardBackground}
        label="hour"
      />

      <Text style={[styles.separator, { color: accentColor, fontFamily: getBodyFont() }]}>
        :
      </Text>

      <TimeScrollColumn
        values={MINUTES}
        selectedValue={snappedMinute}
        onValueChange={handleMinuteChange}
        formatValue={formatMinute}
        accentColor={accentColor}
        textColor={palette.text}
        textMutedColor={palette.textMuted}
        cardBackgroundColor={palette.cardBackground}
        label="minute"
        snapToNearest={5}
      />

      <AmPmToggle
        isAm={isAm}
        onChange={handlePeriodChange}
        accentColor={accentColor}
        textColor={palette.text}
        textMutedColor={palette.textMuted}
        cardBackgroundColor={palette.cardBackground}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.lg,
  },
  separator: {
    fontSize: 28,
    fontWeight: '700',
    marginHorizontal: Spacing.sm,
  },
});
